import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { useState } from 'react';
import { BlogBlueprintsRunPayload } from '../../types/blog-generation-agent.types';
import { createBlogPostBlueprints } from 'src/common/Scraper';
import useUpdateUserAgents from '../../hooks/useUpdateUserAgents';
import { sleep } from 'src/common/config/FunctionHelper';

import { posthogService, PostHogEvent } from 'src/common/services/posthogService';

/**
 * A custom hook to manage blog blueprint creation mutation state and handlers.
 * 
 * @param userId - The user ID.
 * @param siteId - The site ID.
 * @returns An object containing the mutation state, pending status, and execution handler.
 */

interface UseBlogGenerationMutationProps {
  topics?: string[];
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onMutateStart?: () => void;
  siteIdOverride?: string;
  userIdOverride?: string;
}

function useBlogGenerationMutation({ onSuccess, onError, onMutateStart, topics: initialTopics = [], siteIdOverride, userIdOverride }: UseBlogGenerationMutationProps = {}) {
  const queryClient = useQueryClient();
  const { mutate: updateAgents } = useUpdateUserAgents('blog-generation');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [topics, setTopics] = useState<string[]>(initialTopics);

  const { _id: reduxSiteId, user_id: reduxUserId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  );
  const siteId = siteIdOverride || reduxSiteId;
  const userId = userIdOverride || reduxUserId;

  const blogBlueprintsMutation = useMutation({
    mutationFn: (payload: BlogBlueprintsRunPayload) => createBlogPostBlueprints(payload),
    mutationKey: ['create-blog-blueprint', userId, siteId],
    onMutate: (variables) => {
      const targetSiteId = variables?.site_id || siteId;
      if (targetSiteId) {
        queryClient.setQueryData(['agentsStatus', targetSiteId], (oldData: any) => ({
          ...oldData,
          agents: {
            ...oldData?.agents,
            'blog-generation': {
              ...oldData?.agents?.['blog-generation'],
              status: 'processing',
            },
          },
        }));
      }
    },
    onSuccess: (data, variables) => {
      // Track Orbit AI Blog Blueprint generation in PostHog
      // Captures site ID, user ID, and the topics count/list used for blueprint generation
      posthogService.track(PostHogEvent.ORBIT_AI_BLOG_BLUEPRINTS_GENERATED, {
        site_id: variables?.site_id || siteId,
        user_id: variables?.user_id || userId,
        topics_count: variables?.topics?.length || 0,
        topics: variables?.topics || []
      });

      queryClient.invalidateQueries({
        queryKey: ['blog-blueprints', userId, siteId]
      });
      const targetSiteId = variables?.site_id || siteId;
      if (targetSiteId) {
        queryClient.setQueryData(['agentsStatus', targetSiteId], (oldData: any) => ({
          ...oldData,
          agents: {
            ...oldData?.agents,
            'blog-generation': {
              ...oldData?.agents?.['blog-generation'],
              status: 'processing',
            },
          },
        }));
        queryClient.invalidateQueries({
          queryKey: ['agentsStatus', targetSiteId]
        });
      }
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error, variables) => {
      const targetSiteId = variables?.site_id || siteId;
      if (targetSiteId) {
        queryClient.invalidateQueries({
          queryKey: ['agentsStatus', targetSiteId]
        });
      }
      if (onError) {
        onError(error);
      }
      handleAgentStatusUpdate(false, variables?.site_id, variables?.topics, 'blog_generation_dashboard')
    }
  })

  const pendingMutations = useMutationState({
    filters: { mutationKey: ['create-blog-blueprint', userId, siteId], status: 'pending' },
  });

  const isMutationPending = pendingMutations.length > 0;

  function handleAgentStatusUpdate(status: boolean, overrideSiteId?: string, topicKeywords?: string[], source: string = 'blog_generation_dashboard'){
    updateAgents({
      site_id: overrideSiteId || siteId,
      source,
      agents: [
        {
          name: 'blog_generation',
          enabled: status,
          config: {
            topics: status ? (topicKeywords || topics) : [],
            keywords: ['']
          }
        }
      ]
    });
  }

  const handleEnableBlogGeneration = async (kws: string[], overrides: { siteId: string; userId: string, shouldRun?: boolean }) => {
    const effectiveSiteId = overrides?.siteId || siteId;
    const effectiveUserId = overrides?.userId || userId;
    const shouldRun = overrides?.shouldRun ?? true;

    if (onMutateStart) {
      onMutateStart();
    }

    setDialogOpen(false);

    handleAgentStatusUpdate(true, overrides?.siteId, kws);

    if(shouldRun){
      blogBlueprintsMutation.mutate({
        topics: kws,
        user_id: effectiveUserId,
        site_id: effectiveSiteId,
      });
    }
  };

  return {
    dialogOpen,
    setDialogOpen,
    topics,
    setTopics,
    blogBlueprintsMutation,
    isMutationPending,
    handleEnableBlogGeneration,
    updateBlogAgentStatus: handleAgentStatusUpdate
  };
}

export default useBlogGenerationMutation;
